import { useState } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import Footer from "../components/Footer";

const AVATAR_COLORS = ["#2563EB", "#7C3AED", "#059669", "#D97706", "#DC2626", "#0891B2", "#0D9488", "#6D28D9"];

function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: "flex", gap: "6px" }}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "2px", transition: "transform 0.1s",
            transform: (hover || value) >= n ? "scale(1.15)" : "scale(1)",
          }}
        >
          <svg width="30" height="30" viewBox="0 0 24 24"
            fill={(hover || value) >= n ? "#F59E0B" : "none"}
            stroke={(hover || value) >= n ? "#F59E0B" : "rgba(1,46,114,0.2)"}
            strokeWidth="1.5"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>
      ))}
    </div>
  );
}

const fieldLabelStyle = {
  fontFamily: "'Cinzel', serif",
  fontSize: "8.5px",
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: "rgba(1,46,114,0.4)",
  display: "block",
  marginBottom: "8px",
};

const inputStyle = {
  width: "100%",
  padding: "11px 14px",
  fontFamily: "'Constantia', 'Georgia', serif",
  fontSize: "14px",
  color: "#012E72",
  border: "1px solid rgba(1,46,114,0.14)",
  borderRadius: "10px",
  outline: "none",
  boxSizing: "border-box",
  backgroundColor: "#fff",
  transition: "border-color 0.15s",
};

export default function TestimonialSubmitPage() {
  usePageTitle("Share Your Experience");
  const [form, setForm] = useState({ name: "", rating: 5, message: "" });
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setErrorMsg("Please enter your name."); return; }
    if (!form.message.trim()) { setErrorMsg("Please describe your experience."); return; }
    setStatus("loading");
    setErrorMsg("");
    try {
      const initials = form.name.trim().split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
      const avatarBg = AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];
      const timeLabel = new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/testimonials/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name:      form.name.trim(),
          initials,
          avatarBg,
          rating:    form.rating,
          text:      form.message.trim(),
          timeLabel,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Submission failed.");
      setStatus("success");
    } catch (err) {
      setErrorMsg(err.message);
      setStatus(null);
    }
  };

  if (status === "success") {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#F7F4EF" }}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 24px" }}>
          <div style={{ textAlign: "center", maxWidth: "480px" }}>
            <div style={{
              width: "68px", height: "68px", borderRadius: "20px",
              backgroundColor: "#D1FAE5",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 24px",
            }}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#065F46" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "24px", color: "#012E72", marginBottom: "14px", letterSpacing: "0.04em" }}>
              Thank You!
            </h2>
            <p style={{ fontFamily: "'Constantia', 'Georgia', serif", fontSize: "15px", color: "rgba(1,46,114,0.6)", lineHeight: 1.75 }}>
              Your testimonial has been submitted and is pending review by our team.
              We appreciate you sharing your experience with Paladin.
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#F7F4EF" }}>

      {/* Header band */}
      <div style={{ backgroundColor: "#012E72", padding: "56px 24px 48px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(120deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 40px)", pointerEvents: "none" }} />
        <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.3em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginBottom: "10px" }}>
          Client Stories
        </p>
        <h1 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "clamp(20px, 4vw, 32px)", color: "#fff", letterSpacing: "0.04em", marginBottom: "10px" }}>
          Share Your Experience
        </h1>
        <p style={{ fontFamily: "'Constantia', 'Georgia', serif", fontSize: "15px", color: "rgba(255,255,255,0.55)", maxWidth: "400px", margin: "0 auto" }}>
          Help others by sharing your Paladin experience. Your review helps us improve.
        </p>
      </div>

      {/* Form area */}
      <div style={{ flex: 1, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px 60px" }}>
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100%", maxWidth: "580px",
            backgroundColor: "#fff",
            borderRadius: "20px",
            padding: "36px",
            border: "1px solid rgba(1,46,114,0.09)",
            boxShadow: "0 8px 40px rgba(1,46,114,0.08)",
          }}
        >
          {/* Name */}
          <div style={{ marginBottom: "22px" }}>
            <label style={fieldLabelStyle}>Your Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => set("name", e.target.value)}
              placeholder="Jane Smith"
              style={inputStyle}
              onFocus={e => { e.target.style.borderColor = "#002DB5"; }}
              onBlur={e => { e.target.style.borderColor = "rgba(1,46,114,0.14)"; }}
            />
          </div>

          {/* Rating */}
          <div style={{ marginBottom: "22px" }}>
            <label style={fieldLabelStyle}>Your Rating *</label>
            <StarPicker value={form.rating} onChange={v => set("rating", v)} />
            <p style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "rgba(1,46,114,0.4)", marginTop: "6px", fontStyle: "italic" }}>
              {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][form.rating]}
            </p>
          </div>

          {/* Message */}
          <div style={{ marginBottom: "28px" }}>
            <label style={fieldLabelStyle}>Your Experience *</label>
            <textarea
              rows={5}
              value={form.message}
              onChange={e => set("message", e.target.value)}
              placeholder="Tell us about your experience with Paladin Professional Insurance Solutions…"
              style={{ ...inputStyle, resize: "vertical" }}
              onFocus={e => { e.target.style.borderColor = "#002DB5"; }}
              onBlur={e => { e.target.style.borderColor = "rgba(1,46,114,0.14)"; }}
            />
          </div>

          {errorMsg && (
            <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "#dc2626", marginBottom: "16px" }}>
              {errorMsg}
            </p>
          )}

          <button
            type="submit"
            disabled={status === "loading"}
            style={{
              width: "100%",
              fontFamily: "'Cinzel', serif", fontSize: "9.5px",
              letterSpacing: "0.18em", textTransform: "uppercase",
              color: "#fff",
              background: "linear-gradient(135deg, #012E72, #002DB5)",
              border: "none", borderRadius: "999px",
              padding: "15px",
              cursor: status === "loading" ? "not-allowed" : "pointer",
              opacity: status === "loading" ? 0.6 : 1,
              boxShadow: "0 4px 20px rgba(0,45,181,0.28)",
              transition: "opacity 0.15s",
            }}
            onMouseEnter={e => { if (status !== "loading") e.currentTarget.style.opacity = "0.88"; }}
            onMouseLeave={e => { if (status !== "loading") e.currentTarget.style.opacity = "1"; }}
          >
            {status === "loading" ? "Submitting…" : "Submit Testimonial"}
          </button>

          <p style={{ textAlign: "center", fontFamily: "'Constantia', serif", fontSize: "12px", color: "rgba(1,46,114,0.35)", marginTop: "16px", fontStyle: "italic" }}>
            Testimonials are reviewed before being published.
          </p>
        </form>
      </div>

      <Footer />
    </div>
  );
}
